import React, { memo, ReactElement } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  IconButton,
  Snackbar,
  Typography,
  TextField,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { FeedbackSnackbarState, FeedbackSnackbarActions } from './useFeedbackSnackbar';

export interface FeedbackSnackbarProps extends FeedbackSnackbarState, FeedbackSnackbarActions {}

export const FeedbackSnackbar = memo(function FeedbackSnackbar(props: FeedbackSnackbarProps): ReactElement {
  const { t } = useTranslation();
  const {
    isVisible,
    isLoading,
    isExpanded,
    problemTypes,
    hasSubmittedFeedback,
    handleCheckboxChange,
    handleInputChange,
    handleClose,
    handleReportProblem,
    onSubmit,
  } = props;

  const hasAnyProblem = Object.values(problemTypes).some(Boolean);

  return (
    <Snackbar open={isVisible} onClose={handleClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
      <Card sx={{ borderRadius: '10px' }} elevation={isExpanded ? 8 : 2}>
        <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 0, marginBottom: -1 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Button
              variant='text'
              onClick={handleReportProblem}
              size={fontSize}
              disabled={isExpanded}
              sx={{ padding: 0 }}
            >
              {hasSubmittedFeedback ? t('feedback.editFeedback') : t('feedback.reportProblem')}
            </Button>
            <IconButton size={fontSize} onClick={handleClose} sx={{ color: 'action', padding: 0 }}>
              <Close fontSize={fontSize} />
            </IconButton>
          </Box>
          <Box display={isExpanded ? 'flex' : 'none'} gap={2} flexDirection='column'>
            <Typography variant='body2' color='textSecondary'>
              {t('feedback.description')}
            </Typography>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={problemTypes.image}
                    onChange={handleCheckboxChange('image')}
                    disabled={isLoading}
                  />
                }
                label={t('feedback.problems.image')}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={problemTypes.prediction}
                    onChange={handleCheckboxChange('prediction')}
                    disabled={isLoading}
                  />
                }
                label={t('feedback.problems.prediction')}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={problemTypes.assignment}
                    onChange={handleCheckboxChange('assignment')}
                    disabled={isLoading}
                  />
                }
                label={t('feedback.problems.assignment')}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={problemTypes.other}
                    onChange={handleCheckboxChange('other')}
                    disabled={isLoading}
                  />
                }
                label={t('feedback.problems.other')}
              />

              <Box sx={{ position: 'relative', width: '100%' }}>
                <TextField
                  multiline
                  variant='outlined'
                  placeholder={t('feedback.problems.placeholderComment')}
                  value={problemTypes.comment}
                  onChange={handleInputChange('comment')}
                  fullWidth
                  inputProps={{
                    style: { height: 120, maxHeight: 120, overflow: 'auto', resize: 'none' },
                  }}
                />
              </Box>
            </FormGroup>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 1,
                marginTop: 2,
              }}
            >
              <Button variant='outlined' onClick={handleClose} disabled={isLoading} size={fontSize}>
                {t('feedback.actions.cancel')}
              </Button>
              <Button
                variant='contained'
                onClick={onSubmit}
                disabled={isLoading || !hasAnyProblem}
                size={fontSize}
                startIcon={isLoading ? <CircularProgress size={16} /> : undefined}
              >
                {isLoading ? t('feedback.actions.submitting') : t('feedback.actions.submit')}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Snackbar>
  );
});

const fontSize = 'medium';
